/**
 * Mapowanie liczba -> jezyk fotograficzny (docs/PROMPTING.md § Mapowanie liczba
 * -> jezyk).
 *
 * TO JEST PLIK, KTORY BEDZIE NAJCZESCIEJ ITEROWANY przy poprawianiu jakosci
 * promptow. Dlatego jest plaskim rejestrem: tablica pasm albo slownik
 * `Record<Enum, string>` + jedna cienka funkcja odczytu. Poprawa brzmienia =
 * edycja jednego wiersza tabeli, bez polowania po kodzie.
 *
 * ZASADY (te same, co dla `src/shared/prompt/**`, bo wynik ladu je w promptcie
 * i w `prompt_hash`):
 *  - zero `Date`, `Date.now()`, `Math.random()`,
 *  - zero iteracji po `Object.keys` bez sortowania,
 *  - zero formatowania floatow bez `toFixed(3)` (patrz `formatDecimal`),
 *  - zero DOM i zero `cloudflare:*`.
 *
 * Konwencje geometryczne (spojne z `src/shared/scene/spec.ts`):
 *  - `azimuthDeg`: 0 = os kamery, dodatnie = w strone camera-left, zakres
 *    -180..180; 180 = dokladnie za produktem (backlight),
 *  - `elevationDeg` kamery: -20..90, 0 = poziom produktu, 90 = z gory,
 *  - `elevationDeg` swiatla: -90..90,
 *  - tarcza zegara widziana z gory: produkt w srodku, KAMERA na godzinie 6,
 *    dodatni azymut idzie przez 7, 8, 9 (camera-left) do 12 (za produktem).
 */

import type {
  DepthOfField,
  FocalLengthMm,
  LightDistance,
  LightModifier,
  LightNode,
  LightRole,
  LightingContrast,
  LightingRig,
  LightingShadow,
  SetSurface,
  ShadowKind,
  StripHighlight,
  StyleGrade,
  SurfaceFinish,
  SurfaceMaterial,
} from '../scene/spec';

// ─────────────────────────────────────────────────────────────────────────────
// Formatowanie liczb (determinizm)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Liczba w promptcie. ZAWSZE przez `toFixed(3)`, potem obcinamy zera koncowe —
 * wynik jest deterministyczny co do bajtu, a jednoczesnie czytelny
 * (`4` zamiast `4.000`, `2.5` zamiast `2.500`).
 */
export function formatDecimal(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const fixed = value.toFixed(3);
  if (!fixed.includes('.')) return fixed;
  return fixed.replace(/0+$/, '').replace(/\.$/, '');
}

/** Liczba calkowita w promptcie (stopnie, procenty, milimetry). */
export function formatInt(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Math.round(value).toFixed(0);
}

/** `62` -> `'62 degrees'`. */
export function formatDegrees(value: number): string {
  return `${formatInt(value)} degrees`;
}

/** `35.5` -> `'36 percent'`. */
export function formatPercent(value: number): string {
  return `${formatInt(value)} percent`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pasma (bands) — podstawowy mechanizm tabel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Jedno pasmo tabeli: obowiazuje dla `value < maxExclusive`. Ostatnie pasmo
 * powinno miec `maxExclusive: Number.POSITIVE_INFINITY`.
 */
export type PhraseBand = {
  readonly maxExclusive: number;
  readonly phrase: string;
};

/** Odczyt z tabeli pasm. Pasma MUSZA byc podane rosnaco po `maxExclusive`. */
export function pickBand(bands: readonly PhraseBand[], value: number): string {
  for (const band of bands) {
    if (value < band.maxExclusive) return band.phrase;
  }
  const last = bands[bands.length - 1];
  return last === undefined ? '' : last.phrase;
}

/**
 * Suwak `0..1` -> jedna z etykiet, rowne przedzialy. Uzywane przez "master
 * diale" (`editorialLevel`, `trustSignal`).
 */
export function pickSliderBand(labels: readonly string[], value: number): string {
  if (labels.length === 0) return '';
  const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
  const index = Math.min(labels.length - 1, Math.floor(clamped * labels.length));
  return labels[index] ?? '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Azymut -> pozycja zegarowa / kierunek
// ─────────────────────────────────────────────────────────────────────────────

/** Normalizacja do (-180, 180]. */
function normalizeAzimuth(deg: number): number {
  const wrapped = ((deg % 360) + 360) % 360;
  return wrapped > 180 ? wrapped - 360 : wrapped;
}

/**
 * Godzina na tarczy zegara (1..12), kamera na 6, dodatni azymut w strone
 * camera-left (7, 8, 9 ... 12).
 */
export function azimuthToClockHour(deg: number): number {
  const normalized = normalizeAzimuth(deg);
  const raw = Math.round(6 + normalized / 30);
  const wrapped = ((raw - 1) % 12 + 12) % 12 + 1;
  return wrapped;
}

/** `'8 o'clock'`. */
export function azimuthClockPhrase(deg: number): string {
  return `${formatInt(azimuthToClockHour(deg))} o'clock`;
}

/** Strona kadru. `'centre'` dla azymutu w okolicy osi kamery lub 180. */
export function azimuthSide(deg: number): 'camera-left' | 'camera-right' | 'centre' {
  const normalized = normalizeAzimuth(deg);
  if (Math.abs(normalized) < 8) return 'centre';
  if (Math.abs(normalized) > 172) return 'centre';
  return normalized > 0 ? 'camera-left' : 'camera-right';
}

/**
 * Pasma pozycji katowej. `value` = `Math.abs(azimuthDeg)`.
 * `{side}` jest podmieniane na `camera-left` / `camera-right`.
 */
export const AZIMUTH_POSITION_BANDS: readonly PhraseBand[] = [
  { maxExclusive: 8, phrase: 'frontal, straight down the camera axis' },
  { maxExclusive: 25, phrase: 'just off axis to {side}' },
  { maxExclusive: 55, phrase: 'from {side}' },
  { maxExclusive: 78, phrase: 'from {side}, three-quarter position' },
  { maxExclusive: 105, phrase: 'from directly {side}, at right angles to the camera' },
  { maxExclusive: 145, phrase: 'from behind and {side}, rear three-quarter position' },
  { maxExclusive: 173, phrase: 'from behind and slightly {side}' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'from directly behind the subject' },
];

/** `45` -> `'from camera-left, 45 degrees off axis (8 o'clock)'`. */
export function azimuthPhrase(deg: number): string {
  const normalized = normalizeAzimuth(deg);
  const side = azimuthSide(normalized);
  const position = pickBand(AZIMUTH_POSITION_BANDS, Math.abs(normalized)).replace(
    '{side}',
    side === 'centre' ? 'centre' : side,
  );
  return `${position}, ${formatDegrees(Math.abs(normalized))} off axis (${azimuthClockPhrase(normalized)})`;
}

/**
 * Docelowe brzmienie z dokumentacji:
 * `lightAzimuthPhrase('key', 45)` ->
 * `"key light from camera-left, 45 degrees off axis (8 o'clock)"`.
 */
export function lightAzimuthPhrase(role: LightRole, deg: number): string {
  return `${role} light ${azimuthPhrase(deg)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Softness -> opis modyfikatora
// ─────────────────────────────────────────────────────────────────────────────

export const SOFTNESS_BANDS: readonly PhraseBand[] = [
  { maxExclusive: 0.1, phrase: 'bare hard source, razor-sharp shadow edge' },
  { maxExclusive: 0.25, phrase: 'small hard source, crisp shadow edge' },
  { maxExclusive: 0.45, phrase: 'medium source, defined but not harsh shadow edge' },
  { maxExclusive: 0.65, phrase: 'diffused source, soft shadow edge' },
  { maxExclusive: 0.82, phrase: 'large diffused softbox, soft wrap' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'very large diffused softbox, very soft wrap, almost shadowless' },
];

/** `0.85` -> `'large diffused softbox, very soft wrap'` (klasa brzmienia z docs). */
export function softnessPhrase(softness: number): string {
  return pickBand(SOFTNESS_BANDS, softness);
}

// ─────────────────────────────────────────────────────────────────────────────
// Ratio -> opis kontrastu
// ─────────────────────────────────────────────────────────────────────────────

export const RATIO_BANDS: readonly PhraseBand[] = [
  { maxExclusive: 1.5, phrase: 'flat even illumination, shadows almost fully filled' },
  { maxExclusive: 2.5, phrase: 'gentle modelling, very open shadows' },
  { maxExclusive: 3.5, phrase: 'clear modelling, open shadows' },
  { maxExclusive: 5, phrase: 'defined but open shadows' },
  { maxExclusive: 6.5, phrase: 'contrasty, shadows read dark with detail' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'dramatic, deep shadows with minimal fill' },
];

/** `4` -> `'4:1 key-to-fill, defined but open shadows'`. */
export function ratioPhrase(ratio: number): string {
  return `${formatDecimal(ratio)}:1 key-to-fill, ${pickBand(RATIO_BANDS, ratio)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Elewacja -> opis pozycji kamery / swiatla
// ─────────────────────────────────────────────────────────────────────────────

export const CAMERA_ELEVATION_BANDS: readonly PhraseBand[] = [
  { maxExclusive: -8, phrase: "low hero camera position, looking slightly up at the product" },
  { maxExclusive: 8, phrase: 'camera at product eye level, horizon through the middle of the product' },
  { maxExclusive: 25, phrase: 'slightly raised camera position, looking gently down on the product' },
  { maxExclusive: 50, phrase: 'raised camera position, clearly looking down on the product' },
  { maxExclusive: 72, phrase: 'high three-quarter camera position, looking down on the product' },
  { maxExclusive: 84, phrase: 'steep overhead camera position, close to top-down' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'directly overhead, true top-down flat lay view' },
];

/** `62` -> `'high three-quarter camera position, looking down on the product'`. */
export function cameraElevationPhrase(deg: number): string {
  return pickBand(CAMERA_ELEVATION_BANDS, deg);
}

export const LIGHT_ELEVATION_BANDS: readonly PhraseBand[] = [
  { maxExclusive: -35, phrase: 'from well below the product, strong uplight' },
  { maxExclusive: -8, phrase: 'from slightly below the product' },
  { maxExclusive: 12, phrase: 'level with the product' },
  { maxExclusive: 35, phrase: 'slightly raised above the product' },
  { maxExclusive: 60, phrase: 'raised well above the product' },
  { maxExclusive: 80, phrase: 'high above the product, steeply angled down' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'directly overhead the product' },
];

export function lightElevationPhrase(deg: number): string {
  return pickBand(LIGHT_ELEVATION_BANDS, deg);
}

// ─────────────────────────────────────────────────────────────────────────────
// Ogniskowa + przyslona -> opis obiektywu
// ─────────────────────────────────────────────────────────────────────────────

export const FOCAL_LENGTH_PHRASES: Readonly<Record<FocalLengthMm, string>> = {
  24: '24 mm wide-angle lens, pronounced perspective and visible edge stretch',
  35: '35 mm lens, mild wide-angle perspective',
  50: '50 mm standard lens, natural perspective',
  85: '85 mm short telephoto lens, flattering compression',
  100: '100 mm macro lens, minimal perspective distortion',
  135: '135 mm telephoto lens, strong compression and flat rendering',
  180: '180 mm telephoto macro lens, very flat compressed rendering',
};

export const APERTURE_BANDS: readonly PhraseBand[] = [
  { maxExclusive: 1.9, phrase: 'wide open, paper-thin plane of focus and creamy fall-off' },
  { maxExclusive: 2.9, phrase: 'shallow depth of field, background dissolved' },
  { maxExclusive: 4.5, phrase: 'moderately shallow depth of field, background softly separated' },
  { maxExclusive: 7, phrase: 'balanced depth of field, product sharp front to back' },
  { maxExclusive: 11.5, phrase: 'deep depth of field, everything crisply defined' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'stopped down for maximum depth of field' },
];

export function aperturePhrase(aperture: number): string {
  return `f/${formatDecimal(aperture)}, ${pickBand(APERTURE_BANDS, aperture)}`;
}

/**
 * `lensPhrase(100, 8)` ->
 * `'100 mm macro lens, minimal perspective distortion, at f/8, deep depth of field, everything crisply defined'`.
 */
export function lensPhrase(focalMm: FocalLengthMm, aperture: number): string {
  return `${FOCAL_LENGTH_PHRASES[focalMm]}, at ${aperturePhrase(aperture)}`;
}

export const DEPTH_OF_FIELD_PHRASES: Readonly<Record<DepthOfField, string>> = {
  deep_all_sharp: 'everything in the frame is sharp, front to back',
  shallow_product_sharp: 'only the product is sharp, the surroundings fall away',
  macro_razor: 'razor-thin macro plane of focus on the product detail',
};

// ─────────────────────────────────────────────────────────────────────────────
// Cien
// ─────────────────────────────────────────────────────────────────────────────

export const SHADOW_KIND_PHRASES: Readonly<Record<ShadowKind, string>> = {
  none: 'no visible shadow, the product sits on a shadowless surface',
  soft_contact: 'a soft contact shadow directly under the product',
  hard_directional: 'a hard-edged directional shadow',
  cast_long: 'a long cast shadow stretching across the surface',
  gobo_pattern: 'a patterned gobo shadow falling across the surface',
};

export const SHADOW_OPACITY_BANDS: readonly PhraseBand[] = [
  { maxExclusive: 0.15, phrase: 'barely visible' },
  { maxExclusive: 0.4, phrase: 'light' },
  { maxExclusive: 0.7, phrase: 'clearly readable' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'deep and dense' },
];

/** Pelne zdanie o cieniu, z kierunkiem i dlugoscia gdy cien istnieje. */
export function shadowPhrase(shadow: LightingShadow): string {
  const base = SHADOW_KIND_PHRASES[shadow.kind];
  if (shadow.kind === 'none') return base;
  const density = pickBand(SHADOW_OPACITY_BANDS, shadow.opacity);
  const direction = azimuthPhrase(shadow.directionDeg);
  const length = `${formatPercent(shadow.lengthPct)} of the frame height`;
  return `${base}, ${density}, falling ${direction}, about ${length} long`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Slowniki: podloze, modyfikatory swiatla, rig, kontrast, grade
// ─────────────────────────────────────────────────────────────────────────────

export const SURFACE_MATERIAL_PHRASES: Readonly<Record<SurfaceMaterial, string>> = {
  bamboo: 'pale bamboo slats with visible fibrous grain',
  brushed_steel: 'brushed stainless steel with fine directional grain',
  frosted_glass: 'frosted glass, softly translucent',
  glass: 'clear glass, sharp reflections and refracted edges',
  linen: 'natural linen weave with a soft irregular texture',
  marble: 'honed marble with fine grey veining',
  matte_acrylic: 'matte acrylic sheet, perfectly even and dust-free',
  oak_plank: 'oak plank with open grain and warm tone',
  raw_concrete: 'raw poured concrete with subtle pitting',
  sand: 'fine dry sand with shallow wind ripples',
  seamless_paper: 'seamless studio paper, perfectly even',
  slate: 'dark riven slate with a matte broken surface',
  terrazzo: 'terrazzo with fine mixed aggregate chips',
  travertine: 'travertine stone with soft pitted texture',
  water: 'a shallow film of still water',
};

export const SURFACE_FINISH_PHRASES: Readonly<Record<SurfaceFinish, string>> = {
  matte: 'matte, no specular reflection',
  satin: 'satin sheen, gentle broad reflection',
  polished: 'polished, crisp mirror-like reflection',
  wet: 'wet, glossy with bright specular streaks',
};

/** `surface` -> `'honed marble with fine grey veining, matte, no specular reflection, in #F5F5F5'`. */
export function surfacePhrase(surface: SetSurface): string {
  const sweep = surface.seamlessSweep ? ', curving up into a seamless sweep' : '';
  return `${SURFACE_MATERIAL_PHRASES[surface.material]}, ${SURFACE_FINISH_PHRASES[surface.finish]}, in ${surface.colorHex}${sweep}`;
}

export const LIGHT_MODIFIER_PHRASES: Readonly<Record<LightModifier, string>> = {
  softbox: 'a softbox',
  strip: 'a narrow strip box',
  beauty_dish: 'a beauty dish',
  bare: 'a bare undiffused head',
  scrim: 'a large scrim',
  bounce: 'a bounce card',
  snoot: 'a snoot',
  grid: 'a gridded head',
};

/** `('softbox', 0.85)` -> `'a softbox: large diffused softbox, soft wrap'`. */
export function lightModifierPhrase(modifier: LightModifier, softness: number): string {
  return `${LIGHT_MODIFIER_PHRASES[modifier]} — ${softnessPhrase(softness)}`;
}

export const LIGHT_DISTANCE_PHRASES: Readonly<Record<LightDistance, string>> = {
  close: 'placed close to the product for a rapid light fall-off',
  mid: 'at a moderate distance',
  far: 'placed far back for an even, slow fall-off',
};

export const LIGHTING_RIG_PHRASES: Readonly<Record<LightingRig, string>> = {
  single_softbox: 'a single large softbox as the only source',
  clamshell: 'a clamshell setup: key above, fill directly below',
  three_point: 'a classic three-point setup: key, fill and rim',
  strip_edge: 'two narrow strip boxes raking the product edges',
  hard_sun: 'a single hard sun-like source',
  window_natural: 'soft directional daylight through a large window',
  ring_macro: 'a ring light on the lens axis for even macro coverage',
  gradient_wall: 'a background wash creating a gradient on the back wall',
};

export const LIGHTING_CONTRAST_PHRASES: Readonly<Record<LightingContrast, string>> = {
  flat: 'flat, shadowless contrast',
  soft: 'soft, low contrast',
  balanced: 'balanced commercial contrast',
  punchy: 'punchy contrast with bright highlights',
  dramatic: 'dramatic, high contrast with deep shadows',
};

export const STRIP_HIGHLIGHT_PHRASES: Readonly<Record<StripHighlight, string>> = {
  none: 'no strip highlights on the product edges',
  single_broad: 'one broad specular highlight along the product edge',
  dual_edge: 'two symmetrical specular highlights along both product edges',
  wrap: 'a specular highlight wrapping around the product silhouette',
};

export const STYLE_GRADE_PHRASES: Readonly<Record<StyleGrade, string>> = {
  neutral: 'neutral colour grade, accurate to the product',
  warm_film: 'warm film-like grade with gentle highlight roll-off',
  cool_clinical: 'cool clinical grade, slightly blue neutrals',
  high_key: 'high-key grade, bright and airy',
  low_key: 'low-key grade, dark and moody',
  pastel: 'pastel grade, desaturated and soft',
  contrasty_editorial: 'contrasty editorial grade with rich blacks',
};

/** Temperatura barwowa -> jezyk. */
export const COLOR_TEMP_BANDS: readonly PhraseBand[] = [
  { maxExclusive: 3100, phrase: 'very warm tungsten light' },
  { maxExclusive: 4100, phrase: 'warm light' },
  { maxExclusive: 5000, phrase: 'neutral-warm light' },
  { maxExclusive: 5800, phrase: 'neutral daylight-balanced light' },
  { maxExclusive: 6600, phrase: 'cool daylight' },
  { maxExclusive: Number.POSITIVE_INFINITY, phrase: 'cold blue-shifted light' },
];

export function colorTempPhrase(kelvin: number): string {
  return `${pickBand(COLOR_TEMP_BANDS, kelvin)} (${formatInt(kelvin)} K)`;
}

/** Jedno pelne zdanie opisujace jedno swiatlo — uzywane w sekcji `LIGHTING`. */
export function lightPhrase(light: LightNode): string {
  return [
    lightAzimuthPhrase(light.role, light.azimuthDeg),
    lightElevationPhrase(light.elevationDeg),
    lightModifierPhrase(light.modifier, light.softness),
    LIGHT_DISTANCE_PHRASES[light.distance],
    `at ${formatPercent(light.intensityPct)} power`,
    colorTempPhrase(light.colorTempK),
  ].join(', ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Pomocniki tekstowe
// ─────────────────────────────────────────────────────────────────────────────

/** `'three_quarter_front'` -> `'three quarter front'`. */
export function humanizeToken(token: string): string {
  return token.replace(/_/g, ' ').trim();
}

/** `['a','b','c']` -> `'a, b and c'`. Kolejnosc wejsciowa jest zachowana. */
export function joinList(items: readonly string[]): string {
  const kept = items.filter((item) => item.length > 0);
  if (kept.length === 0) return '';
  if (kept.length === 1) return kept[0] ?? '';
  return `${kept.slice(0, -1).join(', ')} and ${kept[kept.length - 1] ?? ''}`;
}

/** Skleja fragmenty, pomijajac puste; bez kropki na koncu (dokleja kompilator). */
export function joinPhrases(parts: readonly (string | null | undefined)[], separator = '; '): string | null {
  const kept = parts.filter((part): part is string => typeof part === 'string' && part.length > 0);
  if (kept.length === 0) return null;
  return kept.join(separator);
}

// ─────────────────────────────────────────────────────────────────────────────
// Koercja `unknown` -> wartosc (deskryptory dostaja `unknown`)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deskryptory dostaja `value: unknown`, bo kompilator iteruje po nich
 * generycznie. Te cztery funkcje sa jedynym sposobem zawezenia typu w
 * `promptFragment` — bez `any`, bez `as`.
 */
export function asEnumValue<T extends string>(allowed: readonly T[], value: unknown): T | null {
  if (typeof value !== 'string') return null;
  return allowed.find((candidate): candidate is T => candidate === value) ?? null;
}

export function asBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

export function asNumberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function asStringList(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  const strings: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') return null;
    strings.push(item);
  }
  return strings;
}

/**
 * Lista wartosci enumu w kolejnosci ZADEKLAROWANEJ w `allowed` (nie w
 * kolejnosci z wejscia) — determinizm promptu przy parametrach `multi`.
 */
export function asEnumList<T extends string>(allowed: readonly T[], value: unknown): readonly T[] | null {
  const raw = asStringList(value);
  if (raw === null) return null;
  return allowed.filter((candidate) => raw.includes(candidate));
}
